import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminFormPage } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { LineOfBusinessForm } from "../LineOfBusinessForm/LineOfBusinessForm";
import { useLineOfBusinessMutations } from "../api";
import { emptyLobForm } from "../types";
import type { LobFormValues } from "../types";

const FORM_ID = "lob-form";

export const New = (): JSX.Element => {
  const navigate = useNavigate();
  const { create } = useLineOfBusinessMutations();
  const [submitting, setSubmitting] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);
  const nextAction = useRef<"edit" | "new">("edit");

  const handleSubmit = async (values: LobFormValues) => {
    setSubmitting(true);
    const addAnother = nextAction.current === "new";
    try {
      const result: any = await create(values);
      const newId = result?.data?.id;
      if (addAnother) {
        setFormKey((k) => k + 1);
        setToast({ variant: "success", text: `'${values.name}' saved` });
        setSubmitting(false);
      } else {
        // Categories/approvers are managed on Edit, so redirect there.
        navigate(newId ? `../${newId}/edit` : "..", {
          relative: "path",
          state: {
            toast: { variant: "success", text: `'${values.name}' saved` },
          },
        });
      }
    } catch (err) {
      console.error("Failed to create line of business", err);
      setToast({ variant: "danger", text: "Could not save line of business." });
      setSubmitting(false);
    } finally {
      nextAction.current = "edit";
    }
  };

  const triggerAddAnother = () => {
    nextAction.current = "new";
    (
      document.getElementById(FORM_ID) as HTMLFormElement | null
    )?.requestSubmit();
  };

  return (
    <>
      <AdminFormPage
        title="Add a Line of Business"
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        onSaveAndAddAnother={triggerAddAnother}
        isSubmitting={submitting}
      >
        <LineOfBusinessForm
          key={formKey}
          id={FORM_ID}
          initialValues={emptyLobForm}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default New;
